/**
 * LinguistPro — My Support Tickets
 * SPEC-004 · Route: /support/my-tickets (auth required)
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../store/useAuthStore';

interface Ticket {
  id: string;
  ticket_ref: string;
  subject: string;
  category: string;
  status: string;
  admin_note: string | null;
  created_at: string;
  updated_at: string;
}

const STATUS_STYLES: Record<string, string> = {
  open:      'bg-amber-50 text-amber-700 border-amber-200',
  in_review: 'bg-blue-50 text-blue-700 border-blue-200',
  resolved:  'bg-[#00d4b8]/10 text-[#007a6d] border-[#00d4b8]/25',
  closed:    'bg-gray-100 text-gray-400 border-gray-200',
};

const STATUS_LABELS: Record<string, string> = {
  open:      'Open',
  in_review: 'In Review',
  resolved:  'Resolved',
  closed:    'Closed',
};

const CATEGORY_LABELS: Record<string, string> = {
  bug:     'Bug',
  billing: 'Billing',
  feature: 'Feature request',
  other:   'Other',
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-ZA', {
    year: 'numeric', month: 'short', day: 'numeric',
  });
}

export default function SupportMyTicketsPage() {
  const { session } = useAuthStore();
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!session?.access_token) return;

    fetch('/api/support/my-tickets', {
      headers: { Authorization: `Bearer ${session.access_token}` },
    })
      .then(r => r.json())
      .then(data => {
        if (data.error) throw new Error(data.error);
        setTickets(data.tickets ?? []);
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [session]);

  return (
    <div className="min-h-screen bg-gray-50 px-4 py-10">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 mb-1">My Support Tickets</h1>
            <p className="text-gray-500 text-sm">Track the status of your submitted tickets.</p>
          </div>
          <Link
            to="/support/new"
            className="px-4 py-2 rounded-xl bg-[#00d4b8] text-[#0d1117] text-sm font-semibold hover:bg-[#00bfa8] transition-colors"
          >
            New ticket
          </Link>
        </div>

        {/* Content */}
        {loading && (
          <div className="text-center py-16 text-gray-400 text-sm">Loading tickets…</div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600 mb-6">
            {error}
          </div>
        )}

        {!loading && !error && tickets.length === 0 && (
          <div className="text-center py-16">
            <p className="text-gray-400 text-sm mb-4">You haven't logged any tickets yet.</p>
            <Link
              to="/support/new"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-gray-100 text-gray-600 text-sm hover:bg-gray-200 transition-colors"
            >
              Log your first ticket
            </Link>
          </div>
        )}

        {tickets.length > 0 && (
          <div className="space-y-3">
            {tickets.map(ticket => (
              <div
                key={ticket.id}
                className="bg-white border border-gray-200 shadow-sm rounded-xl p-5 hover:border-gray-300 transition-colors"
              >
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <span className="font-mono text-xs text-[#007a6d]">{ticket.ticket_ref}</span>
                      <span className="text-gray-300">·</span>
                      <span className="text-xs text-gray-400">{CATEGORY_LABELS[ticket.category] ?? ticket.category}</span>
                    </div>
                    <h3 className="text-gray-900 font-medium text-sm truncate">{ticket.subject}</h3>
                  </div>
                  <span className={`shrink-0 px-2.5 py-0.5 rounded-full text-xs font-medium border ${STATUS_STYLES[ticket.status] ?? STATUS_STYLES.open}`}>
                    {STATUS_LABELS[ticket.status] ?? ticket.status}
                  </span>
                </div>

                {ticket.admin_note && (
                  <div className="bg-[#00d4b8]/5 border border-[#00d4b8]/20 rounded-lg px-3 py-2.5 mb-3">
                    <p className="text-xs font-medium text-[#007a6d] mb-1">Response from Support</p>
                    <p className="text-sm text-gray-600">{ticket.admin_note}</p>
                  </div>
                )}

                <p className="text-xs text-gray-400">
                  Submitted {formatDate(ticket.created_at)}
                  {ticket.updated_at !== ticket.created_at && ` · Updated ${formatDate(ticket.updated_at)}`}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
